import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Tabs } from '../components/ui/Tabs';
import { StatusBadge } from '../components/StatusBadge';
import { OrderTimeline } from '../components/OrderTimeline';
import { OrderDocuments } from '../components/OrderDocuments';
import { OrderNotes } from '../components/OrderNotes';
import { mockOrders, mockStatusHistory, mockDocuments, mockNotes, mockPayoffCompanies, mockInsuranceCompanies, mockAgentContacts, mockTitleCompanies, mockTitleContacts, mockLenders } from '../data/mockData';
import { OrderStatus, DocumentFolder } from '../types';
export function OrderDetail() {
  const {
    orderId
  } = useParams();
  const navigate = useNavigate();
  const order = mockOrders.find(o => o.id === orderId);
  const orderHistory = mockStatusHistory.filter(h => h.orderId === orderId);
  const orderDocuments = mockDocuments.filter(d => d.orderId === orderId);
  const initialNotes = mockNotes.filter(n => n.orderId === orderId);
  const [orderNotes, setOrderNotes] = useState(initialNotes);
  const [currentStatus, setCurrentStatus] = useState<OrderStatus>(order?.status || 'ordered');
  if (!order) {
    return <div className="text-center py-12" data-id="element-422">
        <p className="text-slate-600" data-id="element-423">Order not found</p>
        <Button variant="secondary" onClick={() => navigate('/orders')} className="mt-4" data-id="element-424">
          Back to Orders
        </Button>
      </div>;
  }
  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const statusOptions = [{
    value: 'ordered',
    label: 'Ordered'
  }, {
    value: 'pending',
    label: 'Pending'
  }, {
    value: 'received',
    label: 'Received'
  }, {
    value: 'reviewed',
    label: 'Reviewed'
  }, {
    value: 'complete',
    label: 'Complete'
  }];
  const handleStatusChange = (newStatus: OrderStatus) => {
    setCurrentStatus(newStatus);
  };
  const handleDocumentUpload = (file: File, folder: DocumentFolder) => {
    console.log('Uploading file:', file.name, 'to folder:', folder);
  };
  const handleDocumentDelete = (documentId: string) => {
    console.log('Deleting document:', documentId);
  };
  const handleAddNote = (content: string) => {
    const newNote = {
      id: `note-${Date.now()}`,
      orderId: orderId || '',
      content,
      author: 'Current User',
      createdAt: new Date().toISOString()
    };
    setOrderNotes(prev => [newNote, ...prev]);
    mockNotes.push(newNote);
  };
  const payoffCompany = order.payoffCompanyId ? mockPayoffCompanies.find(c => c.id === order.payoffCompanyId) : null;
  const insuranceCompany = order.insuranceCompanyId ? mockInsuranceCompanies.find(c => c.id === order.insuranceCompanyId) : null;
  const agentContact = order.agentContactId ? mockAgentContacts.find(a => a.id === order.agentContactId) : null;
  const chosenLender = order.chosenLenderId ? mockLenders.find(l => l.id === order.chosenLenderId) : null;
  const effectiveDateLabel = order.loanType === 'Purchase' ? 'HOI Effective Date (Close Date)' : 'HOI Effective Date (Ordered Date)';
  const titleCompany = order.titleCompanyId ? mockTitleCompanies.find(c => c.id === order.titleCompanyId) : null;
  const titleContact = order.titleContactId ? mockTitleContacts.find(c => c.id === order.titleContactId) : null;
  const titleLender = order.orderType === 'title' && order.chosenLenderId ? mockLenders.find(l => l.id === order.chosenLenderId) : null;
  const tabs = [{
    id: 'timeline',
    label: 'Timeline',
    content: <OrderTimeline history={orderHistory} data-id="element-425" />
  }, {
    id: 'documents',
    label: 'Documents',
    content: <OrderDocuments documents={orderDocuments} onUpload={handleDocumentUpload} onDelete={handleDocumentDelete} data-id="element-426" />
  }, {
    id: 'notes',
    label: 'Notes',
    content: <OrderNotes notes={orderNotes} onAddNote={handleAddNote} data-id="element-427" />
  }];
  return <div className="space-y-6" data-id="element-428">
      <div className="flex items-center justify-between" data-id="element-429">
        <div className="flex items-center space-x-4" data-id="element-430">
          <Button variant="ghost" onClick={() => navigate('/orders')} className="p-2" data-id="element-431">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-432" />
          </Button>
          <div data-id="element-433">
            <h1 className="text-3xl font-bold text-slate-900" data-id="element-434">
              {order.loanName || order.loanNumber}
            </h1>
            <p className="text-slate-600 mt-1" data-id="element-435">
              {order.loanName ? `${order.loanNumber} · ` : ''}
              {order.borrowerName}
            </p>
          </div>
        </div>
        <Button variant="secondary" data-id="element-436">
          <EditIcon className="w-4 h-4 mr-2" data-id="element-437" />
          Edit Order
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-438">
        <div className="lg:col-span-2 space-y-6" data-id="element-439">
          <Card className="p-6" data-id="element-440">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-441">
              Order Details
            </h2>
            <div className="grid grid-cols-2 gap-4" data-id="element-442">
              <div data-id="element-443">
                <p className="text-sm text-slate-600 mb-1" data-id="element-444">Order Type</p>
                <p className="text-sm font-medium text-slate-900 capitalize" data-id="element-445">
                  {order.orderType}
                </p>
              </div>
              <div data-id="element-446">
                <p className="text-sm text-slate-600 mb-1" data-id="element-447">Status</p>
                <StatusBadge status={currentStatus} data-id="element-448" />
              </div>
              <div data-id="element-449">
                <p className="text-sm text-slate-600 mb-1" data-id="element-450">Assigned To</p>
                <p className="text-sm font-medium text-slate-900" data-id="element-451">
                  {order.assignedTo}
                </p>
              </div>
              <div data-id="element-452">
                <p className="text-sm text-slate-600 mb-1" data-id="element-453">Created</p>
                <p className="text-sm font-medium text-slate-900" data-id="element-454">
                  {formatDate(order.createdAt)}
                </p>
              </div>
            </div>

            {order.orderType === 'payoff' && <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-455">
                <h3 className="text-md font-medium text-slate-900 mb-3" data-id="element-456">
                  Payoff Information
                </h3>

                {/* Dates */}
                <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-457">
                  <div data-id="element-458">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-459">Ordered Date</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-460">
                      {formatDate(order.payoffOrderedDate || '')}
                    </p>
                  </div>
                  <div data-id="element-461">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-462">
                      Completed Date
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-463">
                      {formatDate(order.payoffCompletedDate || '')}
                    </p>
                  </div>
                  <div data-id="element-464">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-465">
                      Good Thru Date
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-466">
                      {formatDate(order.goodThroughDate || '')}
                    </p>
                  </div>
                </div>

                {/* Loan Details */}
                <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-467">
                  <div data-id="element-468">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-469">
                      Existing Loan #
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-470">
                      {order.existingLoanNumber || '—'}
                    </p>
                  </div>
                  <div data-id="element-471">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-472">Lien Position</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-473">
                      {order.lienPosition || '—'}
                    </p>
                  </div>
                  <div data-id="element-474">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-475">
                      Liability Type
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-476">
                      {order.liabilityType || '—'}
                    </p>
                  </div>
                </div>

                {/* Payoff Company */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-477">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3" data-id="element-478">
                    Payoff Company
                  </p>
                  <div className="grid grid-cols-2 gap-4" data-id="element-479">
                    <div data-id="element-480">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-481">Company</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-482">
                        {payoffCompany?.name || '—'}
                      </p>
                    </div>
                    <div data-id="element-483">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-484">
                        Is Automated
                      </p>
                      {order.isAutomated ? <span className="inline-flex items-center text-sm font-medium text-emerald-700" data-id="element-485">
                          <CheckCircleIcon className="w-4 h-4 mr-1" data-id="element-486" />
                          Yes
                        </span> : <span className="inline-flex items-center text-sm font-medium text-slate-500" data-id="element-487">
                          <XCircleIcon className="w-4 h-4 mr-1" data-id="element-488" />
                          No
                        </span>}
                    </div>
                    <div data-id="element-489">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-490">Phone</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-491">
                        {order.payoffCompanyPhone || '—'}
                      </p>
                    </div>
                    <div data-id="element-492">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-493">Email</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-494">
                        {order.payoffCompanyEmail || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Borrowers Auth Status */}
                <div className="mt-4" data-id="element-495">
                  <p className="text-sm text-slate-600 mb-1" data-id="element-496">
                    Borrowers Authorization
                  </p>
                  {order.borrowersAuthAttached ? <span className="inline-flex items-center text-sm font-medium text-emerald-700" data-id="element-497">
                      <CheckCircleIcon className="w-4 h-4 mr-1" data-id="element-498" />
                      Attached
                    </span> : <span className="inline-flex items-center text-sm font-medium text-amber-600" data-id="element-499">
                      <XCircleIcon className="w-4 h-4 mr-1" data-id="element-500" />
                      Not attached — required before send
                    </span>}
                </div>
              </div>}

            {order.orderType === 'insurance' && <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-501">
                <h3 className="text-md font-medium text-slate-900 mb-3" data-id="element-502">
                  Insurance Information
                </h3>

                {/* Type & Loan Type */}
                <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-503">
                  <div data-id="element-504">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-505">
                      Insurance Type
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-506">
                      {order.insuranceType}
                    </p>
                  </div>
                  <div data-id="element-507">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-508">Loan Type</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-509">
                      {order.loanType || '—'}
                    </p>
                  </div>
                  <div data-id="element-510">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-511">
                      {effectiveDateLabel}
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-512">
                      {formatDate(order.hoiEffectiveDate || '')}
                    </p>
                  </div>
                </div>

                {/* Insurance Company */}
                <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-513">
                  <div data-id="element-514">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-515">
                      Insurance Company
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-516">
                      {insuranceCompany?.name || '—'}
                    </p>
                  </div>
                  {order.policyNumber && <div data-id="element-517">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-518">
                        Policy Number
                      </p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-519">
                        {order.policyNumber}
                      </p>
                    </div>}
                </div>

                {/* Agent Contact */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 mb-4" data-id="element-520">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3" data-id="element-521">
                    Agent Contact
                  </p>
                  <div className="grid grid-cols-3 gap-4" data-id="element-522">
                    <div data-id="element-523">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-524">Name</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-525">
                        {agentContact?.name || '—'}
                      </p>
                    </div>
                    <div data-id="element-526">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-527">Phone</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-528">
                        {order.agentPhone || '—'}
                      </p>
                    </div>
                    <div data-id="element-529">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-530">Email</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-531">
                        {order.agentEmail || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Mortgagee Clause */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 mb-4" data-id="element-532">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-533">
                    Mortgagee Clause
                  </p>
                  <p className="text-sm font-medium text-slate-900" data-id="element-534">
                    {order.mortgageeClause || '—'}
                  </p>
                  {chosenLender && <p className="text-xs text-slate-500 mt-1" data-id="element-535">
                      From: {chosenLender.name}
                    </p>}
                </div>

                {/* Borrowers Auth Status */}
                <div data-id="element-536">
                  <p className="text-sm text-slate-600 mb-1" data-id="element-537">
                    Borrowers Authorization
                  </p>
                  {order.insuranceBorrowersAuthAttached ? <span className="inline-flex items-center text-sm font-medium text-emerald-700" data-id="element-538">
                      <CheckCircleIcon className="w-4 h-4 mr-1" data-id="element-539" />
                      Attached
                    </span> : <span className="inline-flex items-center text-sm font-medium text-amber-600" data-id="element-540">
                      <XCircleIcon className="w-4 h-4 mr-1" data-id="element-541" />
                      Not attached — required before send
                    </span>}
                </div>
              </div>}

            {order.orderType === 'title' && <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-542">
                <h3 className="text-md font-medium text-slate-900 mb-3" data-id="element-543">
                  Title Information
                </h3>

                {/* Opportunity Fields */}
                <div className="grid grid-cols-2 gap-4 mb-4 bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-544">
                  <div data-id="element-545">
                    <p className="text-xs font-medium text-slate-500 mb-1" data-id="element-546">
                      DOB (from Opportunity)
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-547">
                      {formatDate(order.borrowerDob || '')}
                    </p>
                  </div>
                  <div data-id="element-548">
                    <p className="text-xs font-medium text-slate-500 mb-1" data-id="element-549">
                      Last 4 SSN (from Opportunity)
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-550">
                      {order.borrowerLast4SSN ? `••••${order.borrowerLast4SSN}` : '—'}
                    </p>
                  </div>
                </div>

                {/* Mortgagee Clause */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 mb-4" data-id="element-551">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-552">
                    Mortgagee Clause
                  </p>
                  <p className="text-sm font-medium text-slate-900" data-id="element-553">
                    {order.titleMortgageeClause || '—'}
                  </p>
                  {titleLender && <p className="text-xs text-slate-500 mt-1" data-id="element-554">
                      From: {titleLender.name}
                    </p>}
                </div>

                {/* Title Company */}
                <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-555">
                  <div data-id="element-556">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-557">Title Company</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-558">
                      {titleCompany?.name || '—'}
                    </p>
                  </div>
                </div>

                {/* Title Contact */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-559">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3" data-id="element-560">
                    Title Contact
                  </p>
                  <div className="grid grid-cols-3 gap-4" data-id="element-561">
                    <div data-id="element-562">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-563">Name</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-564">
                        {titleContact?.name || '—'}
                      </p>
                    </div>
                    <div data-id="element-565">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-566">Phone</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-567">
                        {order.titleContactPhone || '—'}
                      </p>
                    </div>
                    <div data-id="element-568">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-569">Email</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-570">
                        {order.titleContactEmail || '—'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>}

            {order.orderType === 'appraisal' && <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-571">
                <h3 className="text-md font-medium text-slate-900 mb-3" data-id="element-572">
                  Appraisal Information
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-573">
                  <div data-id="element-574">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-575">Appraiser</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-576">
                      {order.appraiser}
                    </p>
                  </div>
                  {order.appraisalOrderDate && <div data-id="element-577">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-578">Order Date</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-579">
                        {formatDate(order.appraisalOrderDate)}
                      </p>
                    </div>}
                  {order.propertyAddress && <div className="col-span-2" data-id="element-580">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-581">
                        Property Address
                      </p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-582">
                        {order.propertyAddress}
                      </p>
                    </div>}
                  {order.valuLinkId && <div data-id="element-583">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-584">ValuLink ID</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-585">
                        {order.valuLinkId}
                      </p>
                    </div>}
                </div>
              </div>}
          </Card>

          <Card className="p-6" data-id="element-586">
            <Tabs tabs={tabs} data-id="element-587" />
          </Card>
        </div>

        <div className="space-y-6" data-id="element-588">
          <Card className="p-6" data-id="element-589">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-590">
              Update Status
            </h2>
            <Select options={statusOptions} value={currentStatus} onChange={e => handleStatusChange(e.target.value as OrderStatus)} data-id="element-591" />
            <p className="text-xs text-slate-500 mt-2" data-id="element-592">
              Status changes are automatically timestamped and tracked
            </p>
          </Card>

          <Card className="p-6" data-id="element-593">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-594">
              Quick Stats
            </h2>
            <div className="space-y-3" data-id="element-595">
              <div className="flex justify-between items-center" data-id="element-596">
                <span className="text-sm text-slate-600" data-id="element-597">Documents</span>
                <span className="text-sm font-medium text-slate-900" data-id="element-598">
                  {orderDocuments.length}
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-599">
                <span className="text-sm text-slate-600" data-id="element-600">Notes</span>
                <span className="text-sm font-medium text-slate-900" data-id="element-601">
                  {orderNotes.length}
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-602">
                <span className="text-sm text-slate-600" data-id="element-603">Status Updates</span>
                <span className="text-sm font-medium text-slate-900" data-id="element-604">
                  {orderHistory.length}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>;
}